'use client'
import { useEffect, useRef, useCallback } from 'react'

interface UseIntersectionObserverProps {
  onIntersect: () => void // callback when element becomes visible
  enabled?: boolean // toggle observation on/off
  rootMargin?: string // margin around viewport/root
  threshold?: number // how much element needs to be visible (0-1)
  root?: Element | null // viewport or custom container
  triggerOnce?: boolean // fire either once or multiple times
}

// A hook that observes an element and calls a callback when it becomes
// visible, referenced from:
// https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
export function useIntersectionObserver<T extends HTMLElement>({
  onIntersect,
  enabled = true,
  rootMargin = '0px',
  threshold = 0.1,
  root = null,
  triggerOnce = true,
}: UseIntersectionObserverProps): React.RefObject<T | null> {
  const ref = useRef<T>(null)
  const hasIntersected = useRef(false)

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
      const [entry] = entries
      if (entry.isIntersecting && (!triggerOnce || !hasIntersected.current)) {
        hasIntersected.current = true
        onIntersect()

        // only disconnect if we're triggering once
        if (triggerOnce && entry.target) {
          observer.unobserve(entry.target)
        }
      }
    },
    [onIntersect, triggerOnce],
  )

  useEffect(() => {
    if (!enabled) return

    const element = ref.current
    if (!element) return

    const observer = new IntersectionObserver(handleIntersection, {
      root,
      rootMargin,
      threshold,
    })

    observer.observe(element)

    return () => observer.disconnect()
  }, [enabled, handleIntersection, root, rootMargin, threshold])

  return ref
}

export default useIntersectionObserver
